import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

const DEFAULT_MESSAGE_LEVELS = {
    DEBUG: 10,
    INFO: 20,
    SUCCESS: 25,
    WARNING: 30,
    ERROR: 40,
};

// Duration of the dismiss fade before the empty space is collapsed. The
// `messages__item--hidden` -> `messages__item--closed` transition below keys
// off this; it mirrors the CSS opacity animation the `.messages` SCSS drives.
const MESSAGE_DISMISS_ANIMATION_MS = 330;

interface IMessage {
    level: number;
    message: string;
    tags: string;
    level_tag: string;
    extra_tags: string;
}

type IMessagesProps = Immutable<{
    messages: IMessage[];
}>;

const getMessageByType = (
    extraTags: string,
    level: number,
): React.JSX.Element => {
    if (extraTags === "added-to-cart") {
        return <>{t`Added to Cart`}</>;
    }
    let message = "";
    switch (level) {
        case DEFAULT_MESSAGE_LEVELS.DEBUG:
            message = t`Info`;
            break;
        case DEFAULT_MESSAGE_LEVELS.INFO:
            message = t`Info`;
            break;
        case DEFAULT_MESSAGE_LEVELS.SUCCESS:
            message = t`Success`;
            break;
        case DEFAULT_MESSAGE_LEVELS.WARNING:
            message = t`Warning`;
            break;
        case DEFAULT_MESSAGE_LEVELS.ERROR:
            message = t`Error`;
            break;
        default:
            message = t`Info`;
            break;
    }
    return <>{message}</>;
};

type DismissPhase = "visible" | "hidden" | "closed";

// One dismissible banner item. Owns its dismissal in React state so no
// document-level close handler is needed: on close it adds
// `messages__item--hidden` (fade) + `aria-hidden="true"`, then
// `messages__item--closed` (collapse) after the animation. Content differs per
// site (category label + message shape), so it is passed in; only the dismiss
// behavior + surrounding markup is shared.
const FlashMessageItem = ({
    tags,
    content,
    closeIcon,
}: {
    tags: string;
    content: React.ReactNode;
    closeIcon: React.ReactNode;
}): React.JSX.Element => {
    const [phase, setPhase] = React.useState<DismissPhase>("visible");

    React.useEffect(() => {
        if (phase !== "hidden") {
            return;
        }
        const timer = setTimeout(
            () => setPhase("closed"),
            MESSAGE_DISMISS_ANIMATION_MS,
        );
        return () => clearTimeout(timer);
    }, [phase]);

    // Built by append (not a ternary swap) so the emitted class order matches
    // the legacy `classList.add` sequence exactly — the CSS animations key on
    // these class names.
    let className = `messages__item ${tags}`;
    if (phase !== "visible") {
        className += " messages__item--hidden";
    }
    if (phase === "closed") {
        className += " messages__item--closed";
    }

    return (
        <li
            className={className}
            aria-hidden={phase === "visible" ? undefined : "true"}
        >
            <div className="messages__item__content l-capped-width">
                {content}
                <button
                    className="messages__item__close"
                    onClick={(event) => {
                        event.preventDefault();
                        setPhase("hidden");
                    }}
                >
                    {closeIcon}
                </button>
            </div>
        </li>
    );
};

export type IFlashMessage = {
    /** Appended to the `messages__item` class (Django `message.tags`). */
    tags: string;
    /** The rendered `<p>` content: the category `<span>` + the message body. */
    content: React.ReactNode;
};

// The single shared flash-message banner. Renders the `.messages` aside/list and
// owns each item's dismissal (via FlashMessageItem). Callers supply the per-item
// `<p>` content and the site's close-button icon; every rendered class name is
// shared here so the CSS/GTM hooks stay identical across sites.
export const FlashMessages = ({
    messages,
    closeIcon,
}: {
    messages: readonly IFlashMessage[];
    closeIcon: React.ReactNode;
}): React.JSX.Element => (
    <aside className="messages l-full-width">
        <ul className="messages__list">
            {messages.map((message, i) => (
                <FlashMessageItem
                    key={i}
                    tags={message.tags}
                    content={message.content}
                    closeIcon={closeIcon}
                />
            ))}
        </ul>
    </aside>
);

// Adapter for the `IMessage` shape (the header/PageChrome message queue on
// tempurpedic, pillows, and one800pillows). Renders the shared FlashMessages
// with the `#reviews-no` close icon; dismissal lives in FlashMessages.
export const Messages: React.FC<IMessagesProps> = ({ messages }) => (
    <FlashMessages
        closeIcon={
            <svg
                className={`icon icon--reviews-no`}
                aria-label={`Close`}
                role="img"
            >
                <use xlinkHref={`#reviews-no`} />
                <title>{`Close`}</title>
            </svg>
        }
        messages={messages.map((message) => ({
            tags: message.tags,
            content: (
                <p>
                    <span
                        className={`messages__item__category messages__item__category--${message.level_tag}`}
                    >
                        {getMessageByType(message.extra_tags, message.level)}
                    </span>
                    <span
                        dangerouslySetInnerHTML={{
                            __html: message.message,
                        }}
                    />
                </p>
            ),
        }))}
    />
);
